// Team activity (MANAGE tier) — is anyone actually using this?
//
// Both panels here previously lived at the bottom of Reports and were removed
// on 2026-08-14 because Reports is a SHARED page: a nurse opening it could see
// that administrators were being shown her sign-in times and her quiz scores.
// The objection was to the audience, not to the data — an administrator still
// needs to know whether the team has opened the app at all. So the same two
// panels live here instead, on a MANAGE-tier route, which visibleRoutes() hides
// from every non-admin and canAccess() refuses to enter. Reports still renders
// identically for every role; nothing was un-removed from it.
//
// The page opens with the ADOPTION banner rather than the per-person lists,
// because "has anyone been in this week" is the question that gets asked, and a
// column of rows reading zero answers it slowly. The banner is deliberately
// blunt when the answer is nobody: a quiet dashboard that looks the same when
// the team is working and when it has stopped is worse than no dashboard.
//
// Honesty rules carried over from the originals:
//   * a LOGIN (credentials typed) and a RESUMED session (token restored on page
//     load) are counted apart, so one clinic tab left open all week can never
//     read as five mornings of work;
//   * an empty table says "nothing has been recorded", never "nobody came" —
//     those differ, and the migration may simply not have run.
//
// The third panel, ACTIONS (added 2026-10-26), answers the question the first
// two cannot: opening the app is not the same as working in it. It reads
// audit_events, counts real STEPS rather than rows (one stage move writes four
// rows in the same second), shows every active staff member including the ones
// on zero, and keeps the scheduled job on its own muted line so hundreds of
// automatic changes a morning can never read as a person working. Patient
// names are hidden by default in the per-person list: this is an admin screen
// that may be on a shared display. See src/data/actions.js for the rules.
//
// Every dynamic string goes through esc(); no inline style attributes (strict
// CSP), so bar heights ride on data-h and are applied after render.

import { esc } from '../ui/esc.js';
import { registerView } from './registry.js';
import { store, SLICES } from '../state/store.js';
import { isAdmin } from '../auth.js';
import { fmtNum } from '../metrics/registry.js';
import { loadSignIns, signInTotals, signInDays, adoption } from '../data/logins.js';
import { loadTrainingAdmin, perNurseStats, perQuestionStats, WEAK_TOPIC_PCT } from '../data/training.js';
import { userName, usersMap, loadUsers } from '../data/users.js';
import { register } from '../ui/actions.js';
import { toast } from '../ui/toast.js';
import { csvLine } from './patients.js';
import {
  WEEK_CHOICES,
  weekWindow,
  fmtNairobi,
  groupSteps,
  perPersonStats,
  describeStep,
  loadActions,
  loadPersonActions,
  PERSON_ROW_CAP,
} from '../data/actions.js';

/** Trailing window for the adoption banner. One working week. */
export const ADOPTION_DAYS = 7;
/** Trailing window for the daily strip. Two weeks, so a weekend pattern shows. */
const STRIP_DAYS = 14;

/** @type {{rows: any[], error: any}|null} */
let _signins = null;
/** @type {{data: any, error: any}|null} */
let _training = null;

// ── Actions panel state ──
/** The picked window: 'this' | 'last' | '4w'. Per page visit, not persisted. */
let _actWeek = 'this';
/** @type {{week: string, rows: any[], systemCount: number|null, cases: Record<string, any>, truncated: boolean, error: any}|null} */
let _actions = null;
/** @type {{userId: string, week: string, loading: boolean, rows: any[], cases: Record<string, any>, capped: boolean, error: any}|null} */
let _person = null;
/** Patient names in the person list — OFF until an administrator turns them on. */
let _showNames = false;
/** Supersedes a slow load when the week changes under it. */
let _actSeq = 0;

/** @returns {any} */
function doc() {
  return /** @type {any} */ (globalThis).document || null;
}

/** The signed-in user's role. @returns {string} */
function role() {
  return (store.get(SLICES.SESSION) || {}).role;
}

/**
 * The headline. Written as a sentence rather than a tile because the useful
 * answer is comparative ("2 of 6 people, on 3 of the last 7 days"), and a bare
 * number invites the reader to supply the flattering denominator themselves.
 * @param {any[]} rows @param {number} staff @param {Date} now @returns {string}
 */
export function adoptionHTML(rows, staff, now) {
  const a = adoption(rows, ADOPTION_DAYS, now);
  const nobody = a.people === 0;
  const last = a.last ? new Date(a.last) : null;
  const lastTxt = last && !isNaN(last.getTime()) ? last.toLocaleString() : null;
  const headline = nobody
    ? `No one has opened the app in the last ${ADOPTION_DAYS} days.`
    : `${fmtNum(a.people)}${staff ? ' of ' + fmtNum(staff) : ''} ${a.people === 1 ? 'person has' : 'people have'} ` +
      `opened the app in the last ${ADOPTION_DAYS} days, on ${fmtNum(a.activeDays)} of those days.`;
  const detail = nobody
    ? lastTxt
      ? `The most recent entry of any kind was ${esc(lastTxt)}.`
      : 'Nothing has ever been recorded, so this may be the audit rather than the team — check the ' +
        'app_logins migration has run before drawing a conclusion.'
    : `${fmtNum(a.logins)} fresh sign-in${a.logins === 1 ? '' : 's'} and ` +
      `${fmtNum(a.entries - a.logins)} resumed session${a.entries - a.logins === 1 ? '' : 's'}. ` +
      'A resumed session is a tab that was already open — it counts as someone looking at the screen, ' +
      'but not as a morning of work.';
  return (
    `<section class="tm-banner${nobody ? ' quiet' : ''}">` +
    `<p class="tm-headline">${esc(headline)}</p>` +
    `<p class="tm-detail">${detail}</p>` +
    '</section>'
  );
}

/**
 * Sign-in activity — WHO opened the app, WHEN, and from what kind of device.
 * @param {Date} now @returns {string}
 */
function signInPanelHTML(now) {
  const head = '<div class="rpt-sec-h"><h2>Sign-in activity</h2><span class="rpt-ln"></span></div>';
  const wrap = (/** @type {string} */ inner) => head + '<section class="rpt-si">' + inner + '</section>';

  if (!_signins) return wrap('<p class="rpt-si-empty">Loading sign-ins…</p>');
  if (_signins.error) {
    return wrap(
      '<p class="rpt-si-empty">Sign-ins could not be loaded. If the app_logins migration ' +
        '(20260801210000) has not been run on this database yet, nothing is being recorded — run it first.</p>'
    );
  }
  const rows = _signins.rows || [];
  if (!rows.length) {
    return wrap(
      '<p class="rpt-si-empty">No sign-ins recorded yet. Entries appear from the next time anyone opens the app — ' +
        'history before the audit was switched on cannot be reconstructed.</p>'
    );
  }

  const days = signInDays(rows, STRIP_DAYS, now);
  const peak = days.reduce((m, d) => Math.max(m, d.n), 0) || 1;
  const strip =
    '<div class="rpt-si-strip" role="img" aria-label="' +
    esc(`Sign-ins per day, last ${STRIP_DAYS} days: ${days.map((d) => `${d.label} ${d.n}`).join(', ')}`) +
    '">' +
    days
      .map(
        (d) =>
          '<span class="rpt-si-day"><span class="rpt-si-colwrap">' +
          `<span class="rpt-si-col${d.n ? '' : ' zero'}" data-h="${Math.round((d.n / peak) * 100)}"></span>` +
          `</span><span class="rpt-si-dl">${esc(d.label)}</span></span>`
      )
      .join('') +
    '</div>';

  const who =
    '<div class="rpt-si-tot">' +
    signInTotals(rows)
      .map((u) => {
        const last = u.last ? new Date(u.last) : null;
        const when = last && !isNaN(last.getTime()) ? last.toLocaleString() : '—';
        return (
          '<div class="rpt-si-person">' +
          `<b>${esc(userName(u.user_id) || u.email || 'Unknown user')}</b>` +
          `<span class="rpt-si-n">${fmtNum(u.logins)} sign-in${u.logins === 1 ? '' : 's'}` +
          `${u.restores ? ` · ${fmtNum(u.restores)} resumed` : ''}</span>` +
          `<span class="rpt-si-last">last: ${esc(when)}</span>` +
          '</div>'
        );
      })
      .join('') +
    '</div>';

  const recent =
    '<div class="rpt-si-list">' +
    rows
      .slice(0, 25)
      .map((r) => {
        const t = r.logged_at ? new Date(r.logged_at) : null;
        const when = t && !isNaN(t.getTime()) ? t.toLocaleString() : '—';
        const dev = /mobile|android|iphone|ipad/i.test(r.user_agent || '') ? 'phone' : 'computer';
        return (
          '<div class="rpt-si-row">' +
          `<b>${esc(userName(r.user_id) || r.email || 'Unknown user')}</b>` +
          `<span class="rpt-si-kind${r.kind === 'restore' ? ' mut' : ''}">` +
          `${r.kind === 'restore' ? 'resumed session' : 'signed in'}</span>` +
          `<span class="rpt-si-when">${esc(when)}</span>` +
          `<span class="rpt-si-dev">${dev}</span>` +
          '</div>'
        );
      })
      .join('') +
    '</div>';

  return wrap(
    '<p class="rpt-si-cap">Every entry into the app, newest first. <b>Signed in</b> means credentials were typed; ' +
      '<b>resumed session</b> means an already-open session was restored — counted apart so a tab left open ' +
      'is never mistaken for a day worked. ' +
      `Showing the latest ${fmtNum(rows.length)} entr${rows.length === 1 ? 'y' : 'ies'}.</p>` +
      strip +
      who +
      recent
  );
}

/**
 * Training progress — WHO has watched and passed WHAT, and which questions the
 * team finds hard. Weak-topic rows are the curation queue: each is a topic that
 * needs a follow-up micro-video or a scenario clinic.
 * @returns {string}
 */
function trainingPanelHTML() {
  const head = '<div class="rpt-sec-h"><h2>Training progress</h2><span class="rpt-ln"></span></div>';
  const wrap = (/** @type {string} */ inner) => head + '<section class="rpt-si rpt-tp">' + inner + '</section>';

  if (!_training) return wrap('<p class="rpt-si-empty">Loading training activity…</p>');
  if (_training.error) {
    return wrap(
      '<p class="rpt-si-empty">Training activity could not be loaded. If the training packs (92/94) have not been ' +
        'run on this database yet, nothing is being recorded — run them first.</p>'
    );
  }
  const d = _training.data || {};
  const nurses = perNurseStats(d.events, d.answers, d.videos, Object.values(usersMap()));
  if (!nurses.length) {
    return wrap(
      '<p class="rpt-si-empty">No training activity yet. Rows appear the moment anyone opens a course part.</p>'
    );
  }
  const totalParts = (d.videos || []).length;
  const nurseRows = nurses
    .map((n) => {
      const last = n.lastActivity ? new Date(n.lastActivity) : null;
      const when = last && !isNaN(last.getTime()) ? last.toLocaleString() : '—';
      return (
        '<div class="rpt-si-row">' +
        `<b>${esc(n.name)}</b>` +
        `<span class="rpt-si-n">${fmtNum(n.parts)} of ${fmtNum(n.totalParts)} parts · ${fmtNum(n.watchMinutes)} min watched</span>` +
        `<span class="rpt-si-kind${n.firstTryPct !== null && n.firstTryPct < WEAK_TOPIC_PCT ? '' : ' mut'}">` +
        `${n.firstTryPct === null ? 'no checks yet' : n.firstTryPct + '% first-try'}</span>` +
        `<span class="rpt-si-when">${n.flagged ? fmtNum(n.flagged) + ' flagged' : '—'}</span>` +
        `<span class="rpt-si-dev">${esc(when)}</span>` +
        '</div>'
      );
    })
    .join('');
  const hard = perQuestionStats(d.answers, d.questions).filter((q) => q.weak);
  const hardRows = hard.length
    ? '<p class="rpt-si-cap"><b>Weak topics — the curation queue.</b> Team first-attempt success below ' +
      `${WEAK_TOPIC_PCT}% (or over 2 attempts on average) flags a question here; each one is a topic that needs a ` +
      'follow-up micro-video or scenario clinic.</p><div class="rpt-si-list">' +
      hard
        .slice(0, 10)
        .map(
          (q) =>
            '<div class="rpt-si-row">' +
            `<b>${esc(q.prompt.length > 90 ? q.prompt.slice(0, 87) + '…' : q.prompt)}</b>` +
            `<span class="rpt-si-kind">${q.firstTryPct === null ? '—' : q.firstTryPct + '% first-try'}</span>` +
            `<span class="rpt-si-when">${q.avgAttempts === null ? '—' : q.avgAttempts + ' avg attempts'}</span>` +
            `<span class="rpt-si-dev">${fmtNum(q.answered)} answered</span>` +
            '</div>'
        )
        .join('') +
      '</div>'
    : '<p class="rpt-si-cap">No weak topics — every question is above the ' +
      WEAK_TOPIC_PCT +
      '% first-attempt line.</p>';
  return wrap(
    `<p class="rpt-si-cap">The course has ${fmtNum(totalParts)} parts. Completion = watched + check passed; ` +
      '<b>flagged</b> counts questions missed three times (they reappear in that nurse’s certification exam). ' +
      'Scores stay on this page — they are never shown to colleagues.</p>' +
      '<div class="rpt-si-list">' +
      nurseRows +
      '</div>' +
      hardRows
  );
}

// ─── Actions — what each person actually did ───────────────────────────────

/** 'coordinator' → 'Coordinator'. @param {any} r @returns {string} */
function roleLabel(r) {
  const s = String(r || '').replace(/_/g, ' ').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : '—';
}

/** @param {number} n @param {string} one @param {string} [many] @returns {string} */
function plural(n, one, many) {
  return `${fmtNum(n)} ${n === 1 ? one : many || one + 's'}`;
}

/**
 * The headline, as blunt as the adoption banner. Three states that must never
 * blur: people worked; the audit is running but nobody worked (the scheduled
 * job wrote rows, so the plumbing is fine and the silence is the team);
 * nothing at all was written, which may be the audit, not the team.
 * @param {any[]} steps from groupSteps
 * @param {ReturnType<typeof perPersonStats>} stats
 * @param {{id: string, label: string, phrase: string}} win
 * @param {number|null} systemCount
 * @returns {{headline: string, detail: string, quiet: boolean}}
 */
export function actionsSummary(steps, stats, win, systemCount) {
  const people = stats.people || [];
  const worked = people.filter((p) => p.steps > 0);
  const lead = win.id === 'this' ? 'This week' : win.id === 'last' ? 'Last week' : 'Last 4 weeks';
  if (!worked.length) {
    if (!systemCount) {
      return {
        headline: `Nothing has been recorded ${win.phrase}.`,
        detail:
          systemCount === 0
            ? 'Not even the scheduled job wrote an entry in this window, so this may be the audit trail rather than ' +
              'the team — check the database is recording before drawing a conclusion.'
            : 'The scheduled job’s count could not be read either, so this may be the audit trail rather than the team.',
        quiet: true,
      };
    }
    return {
      headline: `Nobody recorded any work in the app ${win.phrase}.`,
      detail:
        `The scheduled job wrote ${plural(systemCount, 'automatic change')} in the same window, so the audit trail ` +
        'is working — the silence is the team, not the plumbing.',
      quiet: true,
    };
  }
  const nurses = people.filter((p) => p.nurse && p.active);
  const nurseIds = new Set(nurses.map((p) => p.userId));
  const nursesWorked = nurses.filter((p) => p.steps > 0);
  const patients = new Set(
    (steps || []).filter((s) => s && s.actor && nurseIds.has(s.actor) && s.caseId).map((s) => s.caseId)
  ).size;
  let headline;
  if (nurses.length) {
    headline =
      `${lead}: ${fmtNum(nursesWorked.length)} of ${plural(nurses.length, 'nurse')} did any work in the app ` +
      `(${plural(patients, 'patient')}).`;
  } else {
    const all = new Set((steps || []).filter((s) => s && s.actor && s.caseId).map((s) => s.caseId)).size;
    headline = `${lead}: ${plural(worked.length, 'person', 'people')} did work in the app (${plural(all, 'patient')}).`;
  }
  const admins = worked.filter((p) => p.known && p.active && !p.nurse).length;
  const former = worked.filter((p) => !p.known || !p.active).length;
  const extra = [];
  if (nurses.length && admins) extra.push(`${plural(admins, 'administrator')} also recorded work.`);
  if (former) extra.push(`${plural(former, 'inactive or unknown account')} also recorded work.`);
  const total = worked.reduce((n, p) => n + p.steps, 0);
  const detail =
    (extra.length ? extra.join(' ') + ' ' : '') +
    `${plural(total, 'step')} in all. A step is one thing done to one patient: a stage move writes several audit ` +
    'entries in the same second and is counted once.';
  return { headline, detail, quiet: nursesWorked.length === 0 && nurses.length > 0 };
}

/** CSV columns, in table order. */
export const ACTIONS_CSV_HEADERS = Object.freeze([
  'Name',
  'Role',
  'Patients worked on',
  'Stage moves',
  'Notes',
  'Tasks created',
  'Other',
  'Last action (Nairobi)',
]);

/**
 * The table as CSV: one line per person, then the scheduled job on its own line
 * (its count in Other, labelled as automatic). Every cell goes through csvLine,
 * which csvSafe()s it. Pure.
 * @param {ReturnType<typeof perPersonStats>} stats
 * @param {number|null} systemCount
 * @returns {string}
 */
export function actionsCsv(stats, systemCount) {
  const lines = [csvLine(ACTIONS_CSV_HEADERS)];
  for (const p of (stats && stats.people) || []) {
    lines.push(
      csvLine([
        p.name + (p.active ? '' : ' (inactive)'),
        roleLabel(p.role),
        p.patients,
        p.stage,
        p.note,
        p.task,
        p.other,
        p.last ? fmtNairobi(p.last) : '',
      ])
    );
  }
  lines.push(
    csvLine([
      'System (scheduled job)',
      'automatic — not a person',
      '',
      '',
      '',
      '',
      systemCount === null || systemCount === undefined ? 'unknown' : systemCount,
      '',
    ])
  );
  return lines.join('\r\n');
}

/** @param {string} id @param {string} label @param {string} cur @returns {string} */
function weekBtn(id, label, cur) {
  const on = id === cur;
  return (
    `<button type="button" class="rpt-seg-b${on ? ' on' : ''}" data-action="tm-week" data-week="${esc(id)}" ` +
    `aria-pressed="${on ? 'true' : 'false'}">${esc(label)}</button>`
  );
}

/**
 * One person's list for the window, grouped per step and per Nairobi day.
 * The patient is shown by REFERENCE NUMBER; names (in the reference cell and
 * inside any typed text) appear only with the toggle on.
 * @param {any} person @param {any} row the person's table row (name) @param {boolean} showNames
 * @param {{phrase: string}} win
 * @returns {string}
 */
function personHTML(person, row, showNames, win) {
  const name = (row && row.name) || 'Unknown user';
  const head =
    '<div class="tm-person-head">' +
    `<h3 id="tm-person-h" tabindex="-1">${esc(name)} — ${esc(win.phrase)}</h3>` +
    '<span class="tm-person-tools">' +
    `<button type="button" class="rpt-seg-b tm-names${showNames ? ' on' : ''}" data-action="tm-names" ` +
    `aria-pressed="${showNames ? 'true' : 'false'}">Show patient names</button>` +
    '<button type="button" class="btn sm" data-action="tm-person-close">Close</button>' +
    '</span></div>';
  const wrap = (/** @type {string} */ inner) =>
    `<section class="tm-person" id="tm-person" aria-labelledby="tm-person-h">${head}${inner}</section>`;
  if (person.loading) return wrap('<p class="rpt-si-empty">Loading…</p>');
  if (person.error) {
    return wrap('<p class="rpt-si-empty">This list could not be loaded. Press Sync, then open the name again.</p>');
  }
  const steps = groupSteps(person.rows || []);
  if (!steps.length) return wrap(`<p class="rpt-si-empty">No recorded work ${esc(win.phrase)}.</p>`);
  /** @type {Array<{day: string, items: string[]}>} */
  const days = [];
  for (const s of steps) {
    const day = fmtNairobi(s.at, { time: false });
    let g = days[days.length - 1];
    if (!g || g.day !== day) days.push((g = { day, items: [] }));
    const c = s.caseId ? (person.cases || {})[s.caseId] : null;
    const ref = !s.caseId
      ? '<span class="tm-step-ref mut">no patient</span>'
      : c && c.gone
        ? '<span class="tm-step-ref mut">record removed</span>'
        : `<span class="tm-step-ref">${esc((c && c.ref) || 'no reference')}` +
          (showNames && c && c.name ? ` · ${esc(c.name)}` : '') +
          '</span>';
    const what = describeStep(s, { patientName: c && c.name, showNames });
    g.items.push(
      '<li class="tm-step">' +
        `<span class="tm-step-t">${esc(fmtNairobi(s.at, { date: false }))}</span>` +
        ref +
        `<span class="tm-step-d">${esc(what)}</span>` +
        (s.rows.length > 1 ? `<span class="tm-step-n">${fmtNum(s.rows.length)} entries</span>` : '') +
        '</li>'
    );
  }
  const capNote = person.capped
    ? `<p class="rpt-si-cap">Showing the latest ${fmtNum(PERSON_ROW_CAP)} audit entries only. Older entries in this ` +
      'window are not listed, and the oldest step shown may be missing some of its entries.</p>'
    : '';
  return wrap(
    capNote +
      days
        .map(
          (g) =>
            `<p class="tm-day-h">${esc(g.day)}</p><ol class="tm-steps">${g.items.join('')}</ol>`
        )
        .join('')
  );
}

/**
 * The panel body (everything under the "Actions" heading). Pure over the state
 * passed in, so the tests can render every state without a network.
 * @param {{week: string, actions: any, person: any, showNames: boolean, users: any[]}} st
 * @param {Date} [now]
 * @returns {string}
 */
export function actionsInnerHTML(st, now) {
  const win = weekWindow(st.week, now);
  const bar =
    '<div class="tm-act-bar">' +
    '<div class="rpt-seg" role="group" aria-label="Which week">' +
    WEEK_CHOICES.map((w) => weekBtn(w.id, w.label, win.id)).join('') +
    '</div>' +
    `<span class="tm-act-range">${esc(win.range)} · Nairobi time</span>` +
    (st.actions && !st.actions.error
      ? '<button type="button" class="btn sm tm-act-csv" data-action="tm-csv">Export CSV</button>'
      : '') +
    '</div>';
  const a = st.actions;
  if (!a || a.week !== win.id) return bar + '<p class="rpt-si-empty">Loading actions…</p>';
  if (a.error) {
    return (
      bar +
      '<p class="rpt-si-empty">Actions could not be loaded. The audit trail did not answer — press Sync to try ' +
      'again. Nothing on this panel is a count of zero.</p>'
    );
  }
  const steps = groupSteps(a.rows || []);
  const stats = perPersonStats(steps, st.users || []);
  const sum = actionsSummary(steps, stats, win, a.systemCount);
  const banner =
    `<div class="tm-banner tm-act-sum${sum.quiet ? ' quiet' : ''}">` +
    `<p class="tm-headline">${esc(sum.headline)}</p>` +
    `<p class="tm-detail">${esc(sum.detail)}</p>` +
    '</div>';
  const openId = st.person && st.person.week === win.id ? st.person.userId : null;
  const num = (/** @type {number} */ n) => `<td class="num${n ? '' : ' z'}">${fmtNum(n)}</td>`;
  const body = stats.people
    .map((p) => {
      const open = p.userId === openId;
      return (
        `<tr class="${p.steps ? '' : 'idle'}${open ? ' on' : ''}">` +
        '<th scope="row">' +
        `<button type="button" class="tm-act-name" data-action="tm-person" data-user="${esc(p.userId)}" ` +
        `aria-expanded="${open ? 'true' : 'false'}">${esc(p.name)}</button>` +
        (p.active ? '' : '<span class="tm-act-tag">inactive</span>') +
        `<span class="tm-act-role-m">${esc(roleLabel(p.role))}</span>` +
        '</th>' +
        `<td class="tm-c-role">${esc(roleLabel(p.role))}</td>` +
        num(p.patients) +
        num(p.stage) +
        num(p.note) +
        num(p.task) +
        num(p.other) +
        `<td class="tm-c-last">${p.last ? esc(fmtNairobi(p.last)) : '—'}</td>` +
        '</tr>'
      );
    })
    .join('');
  const table = stats.people.length
    ? '<div class="tm-act-wrap"><table class="tm-act-table">' +
      '<thead><tr><th scope="col">Name</th><th scope="col" class="tm-c-role">Role</th>' +
      '<th scope="col" class="num">Patients worked on</th><th scope="col" class="num">Stage moves</th>' +
      '<th scope="col" class="num">Notes</th><th scope="col" class="num">Tasks created</th>' +
      '<th scope="col" class="num">Other</th><th scope="col" class="tm-c-last">Last action</th></tr></thead>' +
      `<tbody>${body}</tbody></table></div>`
    : '<p class="rpt-si-empty">The staff directory did not load, so there is no one to list. Press Sync.</p>';
  const sysN = a.systemCount;
  const system =
    '<p class="tm-act-sys"><b>System (scheduled job)</b> · ' +
    (typeof sysN === 'number'
      ? `${plural(sysN, 'automatic change')} ${esc(win.phrase)}`
      : 'count unavailable') +
    '. Not a person — never counted in the table above.</p>';
  const trunc = a.truncated
    ? '<p class="rpt-si-cap">This window has more audit entries than one read returns; the counts above cover the ' +
      'newest ones only. Pick a shorter window for exact figures.</p>'
    : '';
  const person =
    openId && st.person
      ? personHTML(st.person, stats.people.find((p) => p.userId === openId), !!st.showNames, win)
      : '';
  return (
    bar +
    banner +
    '<p class="rpt-si-cap">One row per active staff member, zeros included. <b>Notes</b> are reasons written ' +
    'without moving the stage; <b>Other</b> is everything else (contacts, assignments, archives, expenses…). ' +
    'Tap a name for that person’s list — patients appear by reference number unless you turn names on.</p>' +
    trunc +
    table +
    system +
    person
  );
}

/** Module state, as actionsInnerHTML takes it. */
function actionsState() {
  return {
    week: _actWeek,
    actions: _actions,
    person: _person,
    showNames: _showNames,
    users: Object.values(usersMap()),
  };
}

/** @returns {string} */
function actionsPanelHTML() {
  return (
    '<div class="rpt-sec-h"><h2>Actions</h2><span class="rpt-ln"></span></div>' +
    `<section class="rpt-si tm-act" id="tm-actions">${actionsInnerHTML(actionsState())}</section>`
  );
}

/** Re-render just the Actions panel, then put focus where the user was. @param {string} [focusSel] */
function paintActions(focusSel) {
  const d = doc();
  const host = d && d.getElementById('tm-actions');
  if (!host) return;
  host.innerHTML = actionsInnerHTML(actionsState());
  if (focusSel && typeof host.querySelector === 'function') {
    const el = host.querySelector(focusSel);
    if (el && typeof el.focus === 'function') el.focus();
  }
}

/** Load the picked window. Never throws; a stale answer is dropped. @returns {Promise<void>} */
async function fetchActions() {
  const seq = ++_actSeq;
  const win = weekWindow(_actWeek);
  const res = await loadActions(win).catch((e) => ({ ok: false, data: null, error: e }));
  if (seq !== _actSeq) return;
  _actions = res.ok
    ? {
        week: win.id,
        rows: res.data.rows || [],
        systemCount: res.data.systemCount,
        cases: res.data.cases || {},
        truncated: !!res.data.truncated,
        error: null,
      }
    : { week: win.id, rows: [], systemCount: null, cases: {}, truncated: false, error: res.error };
}

/** @param {string} id @returns {Promise<void>} */
async function onWeek(id) {
  if (!isAdmin(role())) return;
  const next = weekWindow(id).id;
  if (next === _actWeek && _actions && _actions.week === next) return;
  _actWeek = next;
  _person = null;
  paintActions(`[data-action="tm-week"][data-week="${next}"]`);
  await fetchActions();
  paintActions(`[data-action="tm-week"][data-week="${next}"]`);
}

/** Open (or close, on a second tap) one person's list. @param {string} uid @returns {Promise<void>} */
async function onPerson(uid) {
  if (!isAdmin(role()) || !uid) return;
  const sel = `[data-action="tm-person"][data-user="${uid.replace(/["\\]/g, '')}"]`;
  if (_person && _person.userId === uid && _person.week === _actWeek) {
    _person = null;
    paintActions(sel);
    return;
  }
  const win = weekWindow(_actWeek);
  _person = { userId: uid, week: win.id, loading: true, rows: [], cases: {}, capped: false, error: null };
  paintActions('#tm-person-h');
  const res = await loadPersonActions({ from: win.from, to: win.to, userId: uid }).catch((e) => ({
    ok: false,
    data: null,
    error: e,
  }));
  if (!_person || _person.userId !== uid || _person.week !== win.id) return; // superseded
  _person = res.ok
    ? { userId: uid, week: win.id, loading: false, rows: res.data.rows || [], cases: res.data.cases || {}, capped: !!res.data.capped, error: null }
    : { userId: uid, week: win.id, loading: false, rows: [], cases: {}, capped: false, error: res.error };
  paintActions('#tm-person-h');
}

function onNames() {
  if (!isAdmin(role())) return;
  _showNames = !_showNames;
  paintActions('[data-action="tm-names"]');
}

function onCsv() {
  if (!isAdmin(role())) {
    toast('CSV export is admin-only', 'err');
    return;
  }
  if (!_actions || _actions.error || _actions.week !== _actWeek) {
    toast('Actions have not loaded — nothing to export', 'err');
    return;
  }
  const win = weekWindow(_actWeek);
  const stats = perPersonStats(groupSteps(_actions.rows), Object.values(usersMap()));
  const csv = actionsCsv(stats, _actions.systemCount);
  const d = doc();
  const g = /** @type {any} */ (globalThis);
  if (!d || !g.Blob || !g.URL) return;
  const blob = new g.Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = g.URL.createObjectURL(blob);
  const a = d.createElement('a');
  a.href = url;
  a.download = `tumaini-team-actions-${win.fileTag}.csv`;
  d.body.appendChild(a);
  a.click();
  d.body.removeChild(a);
  g.URL.revokeObjectURL(url);
  toast(`Exported ${plural(stats.people.length, 'person', 'people')} for ${win.phrase} as CSV`, 'ok');
}

/**
 * Bar heights arrive as data-h because the deployed CSP forbids inline style
 * attributes; they are applied here, after the markup is in the document.
 * @param {any} root @returns {void}
 */
function applyGeometry(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  for (const el of Array.from(root.querySelectorAll('.rpt-si-col[data-h]'))) {
    const v = /** @type {any} */ (el).getAttribute('data-h');
    if (v !== null) /** @type {any} */ (el).style.height = (Number(v) || 0) + '%';
  }
}

/** @param {Date} [now] @returns {string} */
export function teamHTML(now) {
  const clock = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const rows = (_signins && _signins.rows) || [];
  const staff = Object.keys(usersMap()).length;
  return (
    '<div class="rpt-wrap" id="tm-root">' +
    '<header class="rpt-head"><h1>Team activity</h1>' +
    '<p class="rpt-sub">Who has opened the app, how far they have got through the course, and what each ' +
    'person actually did in it. Administrators only — this page is not on any nurse’s menu.</p></header>' +
    (_signins && !_signins.error ? adoptionHTML(rows, staff, clock) : '') +
    signInPanelHTML(clock) +
    trainingPanelHTML() +
    actionsPanelHTML() +
    '</div>'
  );
}

/**
 * Load both panels. Never throws: a failure in one must not cost the other, and
 * neither must ever surface as a blocking dialog on an admin page.
 * @returns {Promise<void>}
 */
async function load() {
  if (!isAdmin(role())) {
    _signins = null;
    _training = null;
    _actions = null;
    _person = null;
    return;
  }
  // Sign-in rows carry a user_id and an email; the NAME lives in the users
  // directory. Without it this page lists raw emails at exactly the moment an
  // admin is trying to recognise their own team.
  try {
    if (!Object.keys(usersMap()).length) await loadUsers();
  } catch {
    /* names degrade to emails; both panels still render */
  }
  // A Sync re-reads the open person's list too, so it never shows rows the
  // refreshed table no longer agrees with: closing it is the honest refresh.
  _person = null;
  const [si, tr] = await Promise.all([
    loadSignIns({ limit: 500 }).catch((e) => ({ ok: false, data: null, error: e })),
    loadTrainingAdmin().catch((e) => ({ ok: false, data: null, error: e })),
    fetchActions(),
  ]);
  _signins = si.ok ? { rows: si.data || [], error: null } : { rows: [], error: si.error };
  _training = tr.ok ? { data: tr.data, error: null } : { data: null, error: tr.error };
  const d = doc();
  const host = d && d.getElementById('tm-root');
  if (host && host.parentElement) {
    host.parentElement.innerHTML = teamHTML();
    applyGeometry(d.getElementById('tm-root'));
  }
}

/** Register the Team activity view. Call once at boot from main.js. @returns {void} */
export function registerTeam() {
  register('tm-week', (el) => void onWeek(el.getAttribute('data-week') || 'this'));
  register('tm-person', (el) => void onPerson(el.getAttribute('data-user') || ''));
  register('tm-person-close', () => {
    const uid = _person && _person.userId;
    _person = null;
    paintActions(uid ? `[data-action="tm-person"][data-user="${uid.replace(/["\\]/g, '')}"]` : undefined);
  });
  register('tm-names', () => onNames());
  register('tm-csv', () => onCsv());
  registerView('team', {
    render(host) {
      host.innerHTML = teamHTML();
      applyGeometry(host);
    },
    load() {
      return load();
    },
  });
}

/** Test seam: drop loaded state so a fresh render shows the loading copy. @returns {void} */
export function _resetTeam() {
  _signins = null;
  _training = null;
  _actWeek = 'this';
  _actions = null;
  _person = null;
  _showNames = false;
}

/**
 * Test seam: inject Actions panel state.
 * @param {{week?: string, actions?: any, person?: any, showNames?: boolean}} st
 * @returns {void}
 */
export function _seedActions(st) {
  if (st.week !== undefined) _actWeek = st.week;
  if (st.actions !== undefined) _actions = st.actions;
  if (st.person !== undefined) _person = st.person;
  if (st.showNames !== undefined) _showNames = st.showNames;
}

/** Test seam: the Actions panel state as the handlers left it. @returns {any} */
export function _actionsSnapshot() {
  return { week: _actWeek, actions: _actions, person: _person, showNames: _showNames };
}

/** Test seam: inject panel state without a network round trip. @param {any} s @param {any} t @returns {void} */
export function _seedTeam(s, t) {
  _signins = s;
  _training = t;
}
